import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Link, Route, Routes } from 'react-router'
import { SessionProvider, useSitzung } from '../session/SessionProvider'
import { lazy, Suspense, type ReactNode } from 'react'
import { AppShell } from '../shell/AppShell'
import { Anmeldung } from '../routes/Anmeldung'
import { Heute } from '../routes/Heute'
import { Familie } from '../routes/Familie'
import { MitgliedRechte } from '../routes/MitgliedRechte'
import { Einrichtung } from '../routes/Einrichtung'
import { Kalender } from '../routes/Kalender'
import { Aktivitaet } from '../routes/Aktivitaet'
import { Posteingang } from '../routes/Posteingang'
import { Mahlzeiten } from '../routes/Mahlzeiten'
import { Stundenplan } from '../routes/Stundenplan'
import { Einstellungen } from '../routes/Einstellungen'
import { Mehr } from '../routes/Mehr'
import { Taschengeld } from '../routes/Taschengeld'
import { Budget } from '../routes/Budget'
import { Kontakte } from '../routes/Kontakte'
import { Benachrichtigungen } from '../routes/Benachrichtigungen'
import { Automatisierungen } from '../routes/Automatisierungen'
import { AutomatisierungEditor } from '../routes/AutomatisierungEditor'
import { Statistiken } from '../routes/Statistiken'
import { Button, ConfirmProvider, Fehlergrenze, SnackbarProvider } from '../ui'
import { bereichErlaubt, bereichMitId } from '../shell/bereiche'
import { darfSehen } from '../session/logic'

const Aufgaben = lazy(() =>
  import('../routes/Aufgaben').then((modul) => ({ default: modul.Aufgaben })),
)
const Einkaufen = lazy(() =>
  import('../routes/Einkaufen').then((modul) => ({ default: modul.Einkaufen })),
)

const qc = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 10_000 },
  },
})

function Bereich({ id, recht, children }: { id: string; recht?: string; children: ReactNode }) {
  const { caps } = useSitzung()
  const darf = recht ? darfSehen(caps, recht) : bereichErlaubt(bereichMitId(id), caps)
  return darf ? (
    <>{children}</>
  ) : (
    <>
      <h1>Bereich nicht freigeschaltet</h1>
      <Link to="/">Zur Startseite</Link>
    </>
  )
}

function Weiche() {
  const { abfrage } = useSitzung()

  if (abfrage.isPending) {
    // Bewusst nur ein Wort statt eines Skeletts: die Antwort kommt in
    // Millisekunden, und ein Platzhalter, der aufblitzt, wirkt unruhiger.
    return (
      <main className="inhalt">
        <p className="unterzeile">Wird geladen …</p>
      </main>
    )
  }
  if (abfrage.isError && abfrage.error.angemeldet)
    return (
      <main className="inhalt">
        <h1>Verbindung fehlgeschlagen</h1>
        <p role="alert">{abfrage.error.message}</p>
        <Button
          onClick={() => {
            void abfrage.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </main>
    )
  if (!abfrage.data) return <Anmeldung />
  if (abfrage.data.setupRequired) return <Einrichtung userName={abfrage.data.user.name} />

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Heute />} />
        <Route
          path="kalender"
          element={
            <Bereich id="kalender">
              <Kalender />
            </Bereich>
          }
        />
        <Route
          path="aufgaben"
          element={
            <Bereich id="aufgaben">
              <Suspense fallback={<p className="unterzeile">Aufgaben werden geladen …</p>}>
                <Aufgaben />
              </Suspense>
            </Bereich>
          }
        />
        <Route
          path="einkaufen"
          element={
            <Bereich id="einkaufen">
              <Suspense fallback={<p className="unterzeile">Einkaufslisten werden geladen …</p>}>
                <Einkaufen />
              </Suspense>
            </Bereich>
          }
        />
        <Route
          path="mahlzeiten"
          element={
            <Bereich id="mahlzeiten">
              <Mahlzeiten />
            </Bereich>
          }
        />
        <Route
          path="stundenplan"
          element={
            <Bereich id="stundenplan">
              <Stundenplan />
            </Bereich>
          }
        />
        <Route
          path="aktivitaet"
          element={
            <Bereich id="aktivitaet">
              <Aktivitaet />
            </Bereich>
          }
        />
        {/* Kein Recht davor: der eigene Posteingang gehoert zur Sitzung. */}
        <Route path="posteingang" element={<Posteingang />} />
        <Route
          path="familie"
          element={
            <Bereich id="familie">
              <Familie />
            </Bereich>
          }
        />
        <Route
          path="familie/:id"
          element={
            <Bereich id="familie">
              <MitgliedRechte />
            </Bereich>
          }
        />
        <Route
          path="taschengeld"
          element={
            <Bereich id="taschengeld">
              <Taschengeld />
            </Bereich>
          }
        />
        <Route
          path="budget"
          element={
            <Bereich id="budget">
              <Budget />
            </Bereich>
          }
        />
        <Route
          path="kontakte"
          element={
            <Bereich id="kontakte">
              <Kontakte />
            </Bereich>
          }
        />
        <Route
          path="statistiken"
          element={
            <Bereich id="statistiken">
              <Statistiken />
            </Bereich>
          }
        />
        <Route
          path="automatisierungen"
          element={
            <Bereich id="automatisierungen">
              <Automatisierungen />
            </Bereich>
          }
        />
        <Route
          path="automatisierungen/editor"
          element={
            <Bereich id="automatisierungen" recht="automation.manage">
              <AutomatisierungEditor />
            </Bereich>
          }
        />
        <Route
          path="automatisierungen/editor/:id"
          element={
            <Bereich id="automatisierungen" recht="automation.manage">
              <AutomatisierungEditor />
            </Bereich>
          }
        />
        <Route path="einstellungen" element={<Einstellungen />} />
        <Route path="benachrichtigungen" element={<Benachrichtigungen />} />
        <Route path="mehr" element={<Mehr />} />
        <Route
          path="*"
          element={
            <>
              <h1>Seite nicht gefunden</h1>
              <Link to="/">Zur Startseite</Link>
            </>
          }
        />
      </Route>
    </Routes>
  )
}

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <SnackbarProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <SessionProvider>
              {/* ⚠️ Die äußere Grenze fängt, was die SCHALE selbst wirft. Ohne
                  sie wäre „Schale kaputt" wieder die weiße Seite. */}
              <Fehlergrenze>
                <Weiche />
              </Fehlergrenze>
            </SessionProvider>
          </BrowserRouter>
        </ConfirmProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  )
}
